/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { logger as log } from '@common/logger';
import { toHeaderCase, toLowerCase } from 'js-convert-case';
import { Client, Provider, cacheExchange, fetchExchange } from 'urql';
import { Graphql } from '@common/graphql';
import { Sttp } from '@common/sttp';
import moment from 'moment';
import { apiConfig } from '@pages/ProvisionalDetailPage/config/api.config';
import { featuresProvisionalDetailConfig } from '../../config/featuresSection.config';
import { GET_TRANSACTION_LIST } from '../../api/graphql';
import { usePolicyId } from '../../provider';
const lookup = require('country-code-lookup');

export interface IGetTransactionListPluginMethods extends IPluginMethodMap {
  getGetTransactionList(args?: any): Promise<any>;
}

export class GetTransactionListPlugin implements IAgentPlugin {
  readonly methods: IGetTransactionListPluginMethods;

  constructor() {
    this.methods = {
      getGetTransactionList: this.getGetTransactionList.bind(this),
    };
  }

  async getGetTransactionList(args?: any) {
    await new Promise((r) => setTimeout(r, 2000));
    await this.findCapsules();
    return {
      //products: this.formatProducts(this.products),
    };
  }

  //Graphql Query example
  async findCapsules(args?: any) {
    const { policyNumber, userVid } = usePolicyId();
    let gql = Graphql.withBaseUrl(apiConfig.spacex_gql.url);
    // const token = getToken();
    const token = null;
    if (token) {
      gql = gql.withHeaders({
        authentication: token,
      });
    }
    const client = gql.getClient();
    try {
      if (client) {
        const res = await client
          .query(GET_TRANSACTION_LIST, {
            policyId: policyNumber,
            limit: 100,
            where: [
              {
                column: 'txnOwner',
                op: 'Equal',
                value: [userVid],
              },
              {
                column: 'type',
                op: 'In',
                value: ['POLICY_PURCHASE_FLOW', 'KYC_SUBMISSION'],
              },
              {
                column: 'txnSetId',
                op: 'In',
                value: [policyNumber, null],
              },
            ],
          })
          .toPromise();
        if (res?.data?.getTxnList) {
          let _txtResult = res?.data?.getTxnList?.data;
          return {
            accordionItems: this.getAccordionItems(_txtResult),
            timelineData: res?.data?.getTxnList,
          };
        }
      }
    } catch (err: any) {
      //log.log('result of capsules=', res?.data?.capsules);
      log.error('error in capsules=', err?.message);
      throw err;
    }
  }

  private getAccordionItems(txList: any) {
    let items: any = [];
    if (txList?.length > 0) {
      let timelineDetails = this.getTimeLine(txList);
      items.push({
        type: 'timeline',
        title: featuresProvisionalDetailConfig?.timelineDetails?.title,
        data: [...timelineDetails],
        lineColor: '#262B40',
        horizontalStepperColors: {
          DONE: '#56DCBA',
          UNDONE: '#56DCBA',
          LAST: '#56DCBA',
          CURRENT: '#56DCBA',
        },
      });
    }
    return items;
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: '#579783',
          children: this.getChildren(element),
        });
      });

    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      multiColList: this.getProvenanceAccordion(data),
      title: toHeaderCase(data?.action ?? ''),
      subtitle: data?.when ? moment(parseInt(data?.when)).format('LLL') : null,
    });

    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.performedBy?.firstName) {
        _list.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName
          )} ${toHeaderCase(res?.performedBy?.lastName)}`,
          icon: featuresProvisionalDetailConfig?.orgIcon,
        });
      }
      if (res?.location) {
        _location.push({
          title: toHeaderCase('at'),
          subTitle: res?.location ?? '-',
          icon: featuresProvisionalDetailConfig?.locationIcon,
        });
      }
      if (res?.when) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.when ? moment(parseInt(res?.when)).format('LLL') : '-',
          icon: featuresProvisionalDetailConfig?.dateIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }
}
